"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createTokenStoreInstruction = exports.initTreasuryInstruction = void 0;
const web3_js_1 = require("@solana/web3.js");
const api_1 = require("./api");
const solana_spl_token_modern_1 = require("solana-spl-token-modern");
/**
 * Generate instruction to init treasury.
 *
 * @type {Object} args - Arguments on instruction creation
 * @param param {IncentivesDistributionProgram} args.program - anchor program instance
 * @param param {PublicKey} args.treasuryAccount - treasury account address [SIGNER] (when not provided, it will be generated)
 * @param param {PublicKey} args.tokenStore - token vault address, at time of ix execution the authority has to be already assigned to treasury token authority
 * @param param {PublicKey} args.adminAuthority - admin authority (default: provider wallet address)
 * @param param {PublicKey} args.rentPayer - rent exception payer [SIGNER] (default: provider wallet address)
 * @param param {PublicKey} args.startTime - timestamp when the treasury starts to be active for claiming
 * @type {Object} return - Return data of generated instruction
 * @return {TransactionInstruction} return.instruction - Instruction to init treasury
 * @return {Keypair} return.keypair - when treasury account was not provided then keypair that was generated is returned
 */
async function initTreasuryInstruction({ program, treasuryAccount = web3_js_1.Keypair.generate(), tokenStore, adminAuthority = program.provider.publicKey, rentPayer = program.provider.publicKey, startTime, }) {
    const treasuryAccountAddress = treasuryAccount instanceof web3_js_1.Keypair
        ? treasuryAccount.publicKey
        : treasuryAccount;
    const instruction = await program.methods
        .initTreasury(adminAuthority, startTime)
        .accountsStrict({
        treasuryAccount: treasuryAccountAddress,
        tokenStore,
        rentPayer,
        systemProgram: web3_js_1.SystemProgram.programId,
    })
        .instruction();
    return {
        keypair: treasuryAccount instanceof web3_js_1.Keypair ? treasuryAccount : undefined,
        instruction,
    };
}
exports.initTreasuryInstruction = initTreasuryInstruction;
/**
 * Generate list of instructions to create account and initialize it with mint.
 *
 * @type {Object} args - Arguments on instruction creation
 * @param param {IncentivesDistributionProgram} args.program - anchor program instance
 * @param param {PublicKey} args.treasuryAccount - treasury account address
 * @param param {PublicKey} args.tokenStore - token vault address [SINGER] that will be created and initialized (default: treasuryTokenAuthorityAddress ATA will be used)
 * @param param {PublicKey} args.tokenMint - token mint the token store will be initialized with
 * @param param {PublicKey} args.rentPayer - rent exception payer [SIGNER] (default: provider wallet address)
 * @type {Object} return - Return data of generated instruction
 * @return {TransactionInstruction} return.instruction - Instruction to init token account
 * @return {Pubkey} return.tokenStore - token store keypair (generated when not provided)
 */
async function createTokenStoreInstruction({ program, treasuryAccount, tokenStore, tokenMint, rentPayer = program.provider.publicKey, }) {
    tokenStore = tokenStore instanceof web3_js_1.Keypair ? tokenStore.publicKey : tokenStore;
    const instructions = [];
    const treasuryTokenAuthority = (0, api_1.treasuryTokenAuthorityAddress)({
        programId: program.programId,
        treasury: treasuryAccount,
    }).address;
    if (tokenStore) {
        // token store address provided, we will initialize it
        instructions.push(web3_js_1.SystemProgram.createAccount({
            fromPubkey: rentPayer,
            newAccountPubkey: tokenStore,
            lamports: await (0, solana_spl_token_modern_1.getMinimumBalanceForRentExemptAccount)(program.provider.connection),
            space: solana_spl_token_modern_1.AccountLayout.span,
            programId: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
        }));
        instructions.push((0, solana_spl_token_modern_1.createInitializeAccount3Instruction)(tokenStore, tokenMint, treasuryTokenAuthority));
    }
    else {
        // token store address not provided, we will use ATA account of treasury token authority
        tokenStore = (0, solana_spl_token_modern_1.getAssociatedTokenAddressSync)(tokenMint, treasuryTokenAuthority, true);
        instructions.push((0, solana_spl_token_modern_1.createAssociatedTokenAccountInstruction)(rentPayer, tokenStore, treasuryTokenAuthority, tokenMint));
    }
    return { instructions, tokenStore };
}
exports.createTokenStoreInstruction = createTokenStoreInstruction;
//# sourceMappingURL=initTreasuryInstruction.js.map